/**
 * Generated by orval 🍺
 * Do not edit manually.
 * SolidOps API
 * OpenAPI spec version: 1.0.0
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import {
  IncidentNotificationResponsePageOutput,
  NotificationPolicyDeleteResponseOutput,
  NotificationPolicyDetailResponseOutput,
  NotificationPolicyPageResponseOutput,
  NotificationRecipientPageResponseOutput,
} from '../model';
import type {
  IncidentErrorResponse,
  ListIncidentNotificationResponsesParams,
  ListNotificationPoliciesParams,
  ListNotificationRecipientOptionsParams,
  ListTeamNotificationPoliciesParams,
  NotificationManagementErrorResponse,
  NotificationPolicyCreate,
  NotificationPolicyDelete,
  NotificationPolicyUpdate,
} from '../model';

import { validatedFetch } from '../../client';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

export const getListIncidentNotificationResponsesUrl = (
  incidentId: string,
  params?: ListIncidentNotificationResponsesParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/v1/incidents/${incidentId}/notification-responses?${stringifiedParams}`
    : `/api/v1/incidents/${incidentId}/notification-responses`;
};

export const listIncidentNotificationResponses = async (
  incidentId: string,
  params?: ListIncidentNotificationResponsesParams,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<IncidentNotificationResponsePageOutput> => {
  return validatedFetch<IncidentNotificationResponsePageOutput>(
    getListIncidentNotificationResponsesUrl(incidentId, params),
    {
      ...options,
      method: 'GET',

      schema: IncidentNotificationResponsePageOutput,
    },
  );
};

export const getListIncidentNotificationResponsesQueryKey = (
  incidentId: string,
  params?: ListIncidentNotificationResponsesParams,
) => {
  return [
    `/api/v1/incidents/${incidentId}/notification-responses`,
    ...(params ? [params] : []),
  ] as const;
};

export const getListIncidentNotificationResponsesQueryOptions = <
  TData = Awaited<ReturnType<typeof listIncidentNotificationResponses>>,
  TError = IncidentErrorResponse,
>(
  incidentId: string,
  params?: ListIncidentNotificationResponsesParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listIncidentNotificationResponses>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof validatedFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListIncidentNotificationResponsesQueryKey(incidentId, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listIncidentNotificationResponses>>> = ({
    signal,
  }) => listIncidentNotificationResponses(incidentId, params, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: incidentId !== null && incidentId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listIncidentNotificationResponses>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListIncidentNotificationResponsesQueryResult = NonNullable<
  Awaited<ReturnType<typeof listIncidentNotificationResponses>>
>;
export type ListIncidentNotificationResponsesQueryError = IncidentErrorResponse;

export function useListIncidentNotificationResponses<
  TData = Awaited<ReturnType<typeof listIncidentNotificationResponses>>,
  TError = IncidentErrorResponse,
>(
  incidentId: string,
  params?: ListIncidentNotificationResponsesParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listIncidentNotificationResponses>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof validatedFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListIncidentNotificationResponsesQueryOptions(
    incidentId,
    params,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}

export const getDeleteNotificationPolicyUrl = (policyId: string) => {
  return `/api/v1/notification-policies/${policyId}`;
};

export const deleteNotificationPolicy = async (
  policyId: string,
  notificationPolicyDelete: NotificationPolicyDelete,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<NotificationPolicyDeleteResponseOutput> => {
  const getHeaders = (
    h?: NonNullable<RequestInit['headers']>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Array.isArray(h)) return Object.fromEntries(h);
    return h;
  };
  return validatedFetch<NotificationPolicyDeleteResponseOutput>(
    getDeleteNotificationPolicyUrl(policyId),
    {
      ...options,
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
      body: JSON.stringify(notificationPolicyDelete),
      schema: NotificationPolicyDeleteResponseOutput,
    },
  );
};

export const getDeleteNotificationPolicyMutationKey = () => ['deleteNotificationPolicy'] as const;

export const getDeleteNotificationPolicyMutationOptions = <
  TError = NotificationManagementErrorResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteNotificationPolicy>>,
    TError,
    DeleteNotificationPolicyMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteNotificationPolicy>>,
  TError,
  DeleteNotificationPolicyMutationVariables,
  TContext
> => {
  const mutationKey = getDeleteNotificationPolicyMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteNotificationPolicy>>,
    DeleteNotificationPolicyMutationVariables
  > = (props) => {
    const { policyId, data } = props ?? {};

    return deleteNotificationPolicy(policyId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteNotificationPolicyMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteNotificationPolicy>>
>;
export type DeleteNotificationPolicyMutationBody = NotificationPolicyDelete;
export type DeleteNotificationPolicyMutationError = NotificationManagementErrorResponse;
export type DeleteNotificationPolicyMutationVariables = {
  policyId: string;
  data: NotificationPolicyDelete;
};

export const useDeleteNotificationPolicy = <
  TError = NotificationManagementErrorResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteNotificationPolicy>>,
    TError,
    DeleteNotificationPolicyMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof deleteNotificationPolicy>>,
  TError,
  DeleteNotificationPolicyMutationVariables,
  TContext
> => {
  return useMutation(getDeleteNotificationPolicyMutationOptions(options));
};
export const getGetNotificationPolicyUrl = (policyId: string) => {
  return `/api/v1/notification-policies/${policyId}`;
};

export const getNotificationPolicy = async (
  policyId: string,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<NotificationPolicyDetailResponseOutput> => {
  return validatedFetch<NotificationPolicyDetailResponseOutput>(
    getGetNotificationPolicyUrl(policyId),
    {
      ...options,
      method: 'GET',

      schema: NotificationPolicyDetailResponseOutput,
    },
  );
};

export const getGetNotificationPolicyQueryKey = (policyId: string) => {
  return [`/api/v1/notification-policies/${policyId}`] as const;
};

export const getGetNotificationPolicyQueryOptions = <
  TData = Awaited<ReturnType<typeof getNotificationPolicy>>,
  TError = NotificationManagementErrorResponse,
>(
  policyId: string,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getNotificationPolicy>>, TError, TData>;
    request?: SecondParameter<typeof validatedFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetNotificationPolicyQueryKey(policyId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getNotificationPolicy>>> = ({ signal }) =>
    getNotificationPolicy(policyId, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: policyId !== null && policyId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getNotificationPolicy>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type GetNotificationPolicyQueryResult = NonNullable<
  Awaited<ReturnType<typeof getNotificationPolicy>>
>;
export type GetNotificationPolicyQueryError = NotificationManagementErrorResponse;

export function useGetNotificationPolicy<
  TData = Awaited<ReturnType<typeof getNotificationPolicy>>,
  TError = NotificationManagementErrorResponse,
>(
  policyId: string,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getNotificationPolicy>>, TError, TData>;
    request?: SecondParameter<typeof validatedFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetNotificationPolicyQueryOptions(policyId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}

export const getUpdateNotificationPolicyUrl = (policyId: string) => {
  return `/api/v1/notification-policies/${policyId}`;
};

export const updateNotificationPolicy = async (
  policyId: string,
  notificationPolicyUpdate: NotificationPolicyUpdate,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<NotificationPolicyDetailResponseOutput> => {
  const getHeaders = (
    h?: NonNullable<RequestInit['headers']>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Array.isArray(h)) return Object.fromEntries(h);
    return h;
  };
  return validatedFetch<NotificationPolicyDetailResponseOutput>(
    getUpdateNotificationPolicyUrl(policyId),
    {
      ...options,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
      body: JSON.stringify(notificationPolicyUpdate),
      schema: NotificationPolicyDetailResponseOutput,
    },
  );
};

export const getUpdateNotificationPolicyMutationKey = () => ['updateNotificationPolicy'] as const;

export const getUpdateNotificationPolicyMutationOptions = <
  TError = NotificationManagementErrorResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateNotificationPolicy>>,
    TError,
    UpdateNotificationPolicyMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateNotificationPolicy>>,
  TError,
  UpdateNotificationPolicyMutationVariables,
  TContext
> => {
  const mutationKey = getUpdateNotificationPolicyMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateNotificationPolicy>>,
    UpdateNotificationPolicyMutationVariables
  > = (props) => {
    const { policyId, data } = props ?? {};

    return updateNotificationPolicy(policyId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateNotificationPolicyMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateNotificationPolicy>>
>;
export type UpdateNotificationPolicyMutationBody = NotificationPolicyUpdate;
export type UpdateNotificationPolicyMutationError = NotificationManagementErrorResponse;
export type UpdateNotificationPolicyMutationVariables = {
  policyId: string;
  data: NotificationPolicyUpdate;
};

export const useUpdateNotificationPolicy = <
  TError = NotificationManagementErrorResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateNotificationPolicy>>,
    TError,
    UpdateNotificationPolicyMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof updateNotificationPolicy>>,
  TError,
  UpdateNotificationPolicyMutationVariables,
  TContext
> => {
  return useMutation(getUpdateNotificationPolicyMutationOptions(options));
};
export const getListNotificationPoliciesUrl = (params?: ListNotificationPoliciesParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/v1/notification-policies?${stringifiedParams}`
    : `/api/v1/notification-policies`;
};

export const listNotificationPolicies = async (
  params?: ListNotificationPoliciesParams,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<NotificationPolicyPageResponseOutput> => {
  return validatedFetch<NotificationPolicyPageResponseOutput>(
    getListNotificationPoliciesUrl(params),
    {
      ...options,
      method: 'GET',

      schema: NotificationPolicyPageResponseOutput,
    },
  );
};

export const getListNotificationPoliciesQueryKey = (params?: ListNotificationPoliciesParams) => {
  return [`/api/v1/notification-policies`, ...(params ? [params] : [])] as const;
};

export const getListNotificationPoliciesQueryOptions = <
  TData = Awaited<ReturnType<typeof listNotificationPolicies>>,
  TError = NotificationManagementErrorResponse,
>(
  params?: ListNotificationPoliciesParams,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof listNotificationPolicies>>, TError, TData>;
    request?: SecondParameter<typeof validatedFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListNotificationPoliciesQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listNotificationPolicies>>> = ({
    signal,
  }) => listNotificationPolicies(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listNotificationPolicies>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListNotificationPoliciesQueryResult = NonNullable<
  Awaited<ReturnType<typeof listNotificationPolicies>>
>;
export type ListNotificationPoliciesQueryError = NotificationManagementErrorResponse;

export function useListNotificationPolicies<
  TData = Awaited<ReturnType<typeof listNotificationPolicies>>,
  TError = NotificationManagementErrorResponse,
>(
  params?: ListNotificationPoliciesParams,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof listNotificationPolicies>>, TError, TData>;
    request?: SecondParameter<typeof validatedFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListNotificationPoliciesQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}

export const getListTeamNotificationPoliciesUrl = (
  teamId: string,
  params?: ListTeamNotificationPoliciesParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/v1/teams/${teamId}/notification-policies?${stringifiedParams}`
    : `/api/v1/teams/${teamId}/notification-policies`;
};

export const listTeamNotificationPolicies = async (
  teamId: string,
  params?: ListTeamNotificationPoliciesParams,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<NotificationPolicyPageResponseOutput> => {
  return validatedFetch<NotificationPolicyPageResponseOutput>(
    getListTeamNotificationPoliciesUrl(teamId, params),
    {
      ...options,
      method: 'GET',

      schema: NotificationPolicyPageResponseOutput,
    },
  );
};

export const getListTeamNotificationPoliciesQueryKey = (
  teamId: string,
  params?: ListTeamNotificationPoliciesParams,
) => {
  return [`/api/v1/teams/${teamId}/notification-policies`, ...(params ? [params] : [])] as const;
};

export const getListTeamNotificationPoliciesQueryOptions = <
  TData = Awaited<ReturnType<typeof listTeamNotificationPolicies>>,
  TError = NotificationManagementErrorResponse,
>(
  teamId: string,
  params?: ListTeamNotificationPoliciesParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listTeamNotificationPolicies>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof validatedFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListTeamNotificationPoliciesQueryKey(teamId, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listTeamNotificationPolicies>>> = ({
    signal,
  }) => listTeamNotificationPolicies(teamId, params, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: teamId !== null && teamId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof listTeamNotificationPolicies>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type ListTeamNotificationPoliciesQueryResult = NonNullable<
  Awaited<ReturnType<typeof listTeamNotificationPolicies>>
>;
export type ListTeamNotificationPoliciesQueryError = NotificationManagementErrorResponse;

export function useListTeamNotificationPolicies<
  TData = Awaited<ReturnType<typeof listTeamNotificationPolicies>>,
  TError = NotificationManagementErrorResponse,
>(
  teamId: string,
  params?: ListTeamNotificationPoliciesParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listTeamNotificationPolicies>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof validatedFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListTeamNotificationPoliciesQueryOptions(teamId, params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}

export const getCreateNotificationPolicyUrl = (teamId: string) => {
  return `/api/v1/teams/${teamId}/notification-policies`;
};

export const createNotificationPolicy = async (
  teamId: string,
  notificationPolicyCreate: NotificationPolicyCreate,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<NotificationPolicyDetailResponseOutput> => {
  const getHeaders = (
    h?: NonNullable<RequestInit['headers']>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Array.isArray(h)) return Object.fromEntries(h);
    return h;
  };
  return validatedFetch<NotificationPolicyDetailResponseOutput>(
    getCreateNotificationPolicyUrl(teamId),
    {
      ...options,
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
      body: JSON.stringify(notificationPolicyCreate),
      schema: NotificationPolicyDetailResponseOutput,
    },
  );
};

export const getCreateNotificationPolicyMutationKey = () => ['createNotificationPolicy'] as const;

export const getCreateNotificationPolicyMutationOptions = <
  TError = NotificationManagementErrorResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createNotificationPolicy>>,
    TError,
    CreateNotificationPolicyMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createNotificationPolicy>>,
  TError,
  CreateNotificationPolicyMutationVariables,
  TContext
> => {
  const mutationKey = getCreateNotificationPolicyMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createNotificationPolicy>>,
    CreateNotificationPolicyMutationVariables
  > = (props) => {
    const { teamId, data } = props ?? {};

    return createNotificationPolicy(teamId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateNotificationPolicyMutationResult = NonNullable<
  Awaited<ReturnType<typeof createNotificationPolicy>>
>;
export type CreateNotificationPolicyMutationBody = NotificationPolicyCreate;
export type CreateNotificationPolicyMutationError = NotificationManagementErrorResponse;
export type CreateNotificationPolicyMutationVariables = {
  teamId: string;
  data: NotificationPolicyCreate;
};

export const useCreateNotificationPolicy = <
  TError = NotificationManagementErrorResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createNotificationPolicy>>,
    TError,
    CreateNotificationPolicyMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createNotificationPolicy>>,
  TError,
  CreateNotificationPolicyMutationVariables,
  TContext
> => {
  return useMutation(getCreateNotificationPolicyMutationOptions(options));
};
export const getListNotificationRecipientOptionsUrl = (
  params: ListNotificationRecipientOptionsParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/v1/notification-recipient-options?${stringifiedParams}`
    : `/api/v1/notification-recipient-options`;
};

export const listNotificationRecipientOptions = async (
  params: ListNotificationRecipientOptionsParams,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<NotificationRecipientPageResponseOutput> => {
  return validatedFetch<NotificationRecipientPageResponseOutput>(
    getListNotificationRecipientOptionsUrl(params),
    {
      ...options,
      method: 'GET',

      schema: NotificationRecipientPageResponseOutput,
    },
  );
};

export const getListNotificationRecipientOptionsQueryKey = (
  params?: ListNotificationRecipientOptionsParams,
) => {
  return [`/api/v1/notification-recipient-options`, ...(params ? [params] : [])] as const;
};

export const getListNotificationRecipientOptionsQueryOptions = <
  TData = Awaited<ReturnType<typeof listNotificationRecipientOptions>>,
  TError = NotificationManagementErrorResponse,
>(
  params: ListNotificationRecipientOptionsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listNotificationRecipientOptions>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof validatedFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListNotificationRecipientOptionsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listNotificationRecipientOptions>>> = ({
    signal,
  }) => listNotificationRecipientOptions(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listNotificationRecipientOptions>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListNotificationRecipientOptionsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listNotificationRecipientOptions>>
>;
export type ListNotificationRecipientOptionsQueryError = NotificationManagementErrorResponse;

export function useListNotificationRecipientOptions<
  TData = Awaited<ReturnType<typeof listNotificationRecipientOptions>>,
  TError = NotificationManagementErrorResponse,
>(
  params: ListNotificationRecipientOptionsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listNotificationRecipientOptions>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof validatedFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListNotificationRecipientOptionsQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}
